(function (global) {
  'use strict';

  const VERSION = '1.0.0';
  const STAGES = Object.freeze(['intake', 'assignment', 'pickup', 'batching', 'routing', 'delivery', 'review', 'learning']);
  const GROUPS = ['dry', 'chilled'];
  const ENGLISH = Object.freeze({
    settings: 'Choose the synthetic scenario', starting_clock: 'Starting clock', readiness: 'SYN-O3 readiness',
    readiness_normal: '10 minutes after start', readiness_late: '25 minutes after start',
    capacity_1: 'SYN-D1 capacity (units)', capacity_2: 'SYN-D2 capacity (units)',
    settings_help: 'Fixed synthetic identifiers only. There are no fields for names, addresses, contact details or real orders. Reset to change the scenario after starting.',
    start: 'Start the simulation', next: 'Continue to {stage}', capture: 'Capture learning record', finished: 'Exercise captured',
    reset: 'Reset exercise', export: 'Export learning JSON',
    stage_intake: 'Order intake', stage_assignment: 'Assignment', stage_pickup: 'Pickup', stage_batching: 'Batching',
    stage_routing: 'Routing', stage_delivery: 'Delivery', stage_review: 'Exception review', stage_learning: 'Learning capture',
    ready: 'Ready. Choose a synthetic scenario and start the exercise.',
    reset_done: 'Exercise reset. The previous in-memory record has been cleared.',
    stage_status: 'Stage {number} of 8: {stage}. {output}',
    intake_output: '{orders} synthetic orders and {drivers} drivers validated.',
    assignment_output: '{assignments}',
    pickup_output: 'Readiness checked. Total pick duration across all orders: {minutes} minutes. Waiting is calculated separately for each driver route.',
    batching_output: '{count} compatible batches. Every batch is within its driver capacity.',
    routing_output: 'Plan ready. Last planned arrival: {clock}, +{elapsed} simulated minutes. Choose an exception or continue.',
    delivery_output: '{arrivals} provisional simulated arrivals; {held} unavailable pickup(s) held. Nothing is accepted until review.',
    review_pending: 'Learner decision required. Review the plan and select accept or hold.',
    review_accepted: 'Simulated record accepted by the learner. Competency remains unassessed.',
    review_held: 'Record held for follow-up. No order is marked accepted. The exception can be captured as learning evidence.',
    learning_output: 'Learning evidence captured. Assessment status: unassessed. Credential issued: no.',
    exception_title: 'Try an exception before delivery',
    exception_help: 'Choose one invented event, or continue with the normal plan. Every run stops for a learner review before learning capture.',
    delay: 'Add a 10-minute delay', missing: 'Mark SYN-O3 pickup missing',
    exception_none: 'No exception added.',
    exception_delay: 'Synthetic delay: add 10 minutes once to SYN-D1 before its first delivery. The plan has been recalculated; learner review is required.',
    exception_missing: 'Synthetic missing pickup: SYN-O3 is held out of the route. It cannot be accepted as delivered. Learner review is required.',
    plan_title: 'Deterministic route plan',
    plan_help: 'Planned times describe this synthetic model. They are not delivery promises. All pickups in a batch finish before that batch departs. Driver clocks run independently.',
    plan_region: 'Synthetic planned arrivals', plan_caption: "Each delivery leg follows the preceding stop for that driver.",
    driver: 'Driver', batch: 'Batch', order: 'Order', wait: 'Ready wait', pick: 'Pick', leg: 'Leg', arrival: 'Planned arrival', outcome: 'Record state',
    group_dry: 'Dry', group_chilled: 'Chilled',
    batch_summary: '{batch}: {orders} · {group} · {units}/{capacity} capacity units · depart {clock}',
    planned: 'Planned only', provisional: 'Simulated arrival; review pending', accepted: 'Accepted simulation record', held: 'Held for follow-up', missing_pickup: 'Held: missing pickup',
    no_arrival: 'No planned arrival', minutes: '{value} min',
    review_title: 'Make the learner review decision',
    review_normal_help: 'Check capacity, separated load groups and cumulative arrival arithmetic. Accept this simulated record, or hold the exercise for follow-up.',
    review_delay_help: 'The invented delay changed SYN-D1 arrival times. Review the recalculated plan before accepting the simulation record, or hold it for follow-up.',
    review_missing_help: 'SYN-O3 has no pickup or arrival. Hold this record for follow-up; it cannot be accepted as a completed delivery run. You can still capture the exception as unassessed learning evidence.',
    approve: 'Accept the simulated record', hold: 'Hold for follow-up',
    review_boundary: 'This records a choice in the exercise. It does not verify reviewer identity, authorize dispatch, clear a safety issue or assess competency.',
    log_title: 'Simulation event log',
    log_help: 'Times are simulated elapsed minutes, not the time you spend on this page. Planning steps occur at +0; the delivery stage advances to the last synthetic arrival.',
    log_time: '+{elapsed} min · {clock}',
    learning_title: 'Exercise captured · Assessment pending',
    learning_help: 'The JSON contains the synthetic inputs, route, exceptions, review choice and event log. Capacity/compatibility, readiness/arrival arithmetic and exception judgment remain unassessed. Rubric ID, rubric version, assessor and scores are empty placeholders. An instructor must define and apply an assessment separately.',
    session_only: 'This session stays in page memory and disappears when you reload or reset. Export if you want a copy. No personal data or token activity is requested.',
    enhancement_unavailable: 'The inputs and stage guide remain available without the interactive tools. When the tools finish loading, you can run and export the synthetic exercise.',
    error_scenario: 'The synthetic scenario is invalid. Reset and use the available choices.',
    error_review_required: 'Make the learner review decision before learning capture.',
    error_missing_cannot_approve: 'A missing pickup cannot be accepted as delivered. Hold the record for follow-up.',
    error_exception_stage: 'An exception can be introduced only at the routing stage, before delivery.',
    error_exception_exists: 'This bounded exercise allows one exception. Reset to try a different event.',
    error_action: 'That action is not available at the current stage.',
    error_export: 'Complete learning capture before exporting.',
    download_ready: 'The synthetic learning JSON download is prepared. Assessment remains unassessed.',
    download_failed: 'This browser could not prepare the download. Keep this page open and try again.'
  });

  const copy = (value) => JSON.parse(JSON.stringify(value));
  const integer = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;

  function createScenario(options) {
    const opts = options || {};
    return {
      id: 'TCN-SYN-001', version: VERSION, simulation: true,
      startMinutes: opts.startMinutes == null ? 480 : opts.startMinutes,
      returnMinutes: 6,
      orders: [
        { id: 'SYN-O1', group: 'dry', units: 2, readyMinutes: 0, pickMinutes: 4, travelMinutes: 12 },
        { id: 'SYN-O2', group: 'dry', units: 1, readyMinutes: 6, pickMinutes: 3, travelMinutes: 8 },
        { id: 'SYN-O3', group: 'chilled', units: 2, readyMinutes: opts.readiness === 'late' ? 25 : 10, pickMinutes: 5, travelMinutes: 15 },
        { id: 'SYN-O4', group: 'dry', units: 2, readyMinutes: 2, pickMinutes: 4, travelMinutes: 6 }
      ],
      drivers: [
        { id: 'SYN-D1', capacity: opts.capacity1 == null ? 4 : opts.capacity1, groups: ['dry'] },
        { id: 'SYN-D2', capacity: opts.capacity2 == null ? 3 : opts.capacity2, groups: ['dry', 'chilled'] }
      ]
    };
  }

  // Reject invalid data instead of silently clamping it. The website exposes only fixed synthetic choices.
  function validateScenario(scenario) {
    const errors = [];
    const data = scenario && typeof scenario === 'object' ? scenario : {};
    if (data.simulation !== true) errors.push('simulation_required');
    if (!integer(data.startMinutes, 0, 1439)) errors.push('invalid_start');
    if (!integer(data.returnMinutes, 0, 180)) errors.push('invalid_return');
    if (!Array.isArray(data.orders) || !data.orders.length || data.orders.length > 4) errors.push('invalid_orders');
    if (!Array.isArray(data.drivers) || !data.drivers.length || data.drivers.length > 2) errors.push('invalid_drivers');
    const orders = Array.isArray(data.orders) ? data.orders : [];
    const drivers = Array.isArray(data.drivers) ? data.drivers : [];
    const seen = new Set();
    for (const entity of orders.concat(drivers)) {
      if (!entity || typeof entity.id !== 'string' || !/^SYN-[A-Z0-9-]{1,20}$/.test(entity.id)) errors.push('invalid_id');
      else if (seen.has(entity.id)) errors.push('duplicate_id');
      else seen.add(entity.id);
    }
    for (const driver of drivers) {
      if (!driver || !integer(driver.capacity, 1, 12)) errors.push('invalid_capacity');
      if (!driver || !Array.isArray(driver.groups) || !driver.groups.length || driver.groups.some((group) => !GROUPS.includes(group)) || new Set(driver.groups).size !== driver.groups.length) errors.push('invalid_driver_groups');
    }
    for (const order of orders) {
      if (!order) { errors.push('invalid_order'); continue; }
      if (!GROUPS.includes(order.group)) errors.push('invalid_group');
      if (!integer(order.units, 1, 12)) errors.push('invalid_units');
      if (!integer(order.readyMinutes, 0, 1440)) errors.push('invalid_readiness');
      if (!integer(order.pickMinutes, 0, 180)) errors.push('invalid_pick');
      if (!integer(order.travelMinutes, 0, 180)) errors.push('invalid_travel');
      if (!drivers.some((driver) => driver && Array.isArray(driver.groups) && driver.groups.includes(order.group) && integer(driver.capacity, 1, 12) && driver.capacity >= order.units)) errors.push('no_eligible_driver');
    }
    return Array.from(new Set(errors));
  }

  function assignOrders(scenario) {
    const errors = validateScenario(scenario);
    if (errors.length) return { valid: false, errors };
    const assignedLoad = {};
    scenario.drivers.forEach((driver) => { assignedLoad[driver.id] = 0; });
    const assignments = scenario.orders.slice().sort((a, b) => a.id.localeCompare(b.id)).map((order) => {
      const eligible = scenario.drivers.filter((driver) => driver.groups.includes(order.group) && driver.capacity >= order.units)
        .sort((a, b) => assignedLoad[a.id] - assignedLoad[b.id] || a.id.localeCompare(b.id));
      const driver = eligible[0];
      assignedLoad[driver.id] += order.units;
      return { orderId: order.id, driverId: driver.id };
    });
    return { valid: true, assignments };
  }

  function buildBatches(scenario, assignments) {
    const errors = validateScenario(scenario);
    if (errors.length) return { valid: false, errors };
    const source = assignments || assignOrders(scenario).assignments;
    if (!Array.isArray(source) || source.length !== scenario.orders.length) return { valid: false, errors: ['invalid_assignments'] };
    const ids = new Set();
    for (const assignment of source) {
      const order = assignment && scenario.orders.find((entry) => entry.id === assignment.orderId);
      const driver = assignment && scenario.drivers.find((entry) => entry.id === assignment.driverId);
      if (!order || !driver || ids.has(assignment.orderId) || driver.capacity < order.units || !driver.groups.includes(order.group)) return { valid: false, errors: ['invalid_assignments'] };
      ids.add(order.id);
    }
    const batches = [];
    for (const driver of scenario.drivers.slice().sort((a, b) => a.id.localeCompare(b.id))) {
      const driverBatches = [];
      const orders = source.filter((assignment) => assignment.driverId === driver.id)
        .map((assignment) => scenario.orders.find((order) => order.id === assignment.orderId))
        .sort((a, b) => a.id.localeCompare(b.id));
      for (const order of orders) {
        let batch = driverBatches.find((entry) => entry.group === order.group && entry.units + order.units <= driver.capacity);
        if (!batch) {
          batch = { id: driver.id + '-B' + (driverBatches.length + 1), driverId: driver.id, group: order.group, capacity: driver.capacity, units: 0, orderIds: [] };
          driverBatches.push(batch);
        }
        batch.orderIds.push(order.id);
        batch.units += order.units;
      }
      batches.push(...driverBatches);
    }
    return { valid: true, batches };
  }

  function validateException(scenario, exception) {
    if (exception == null) return [];
    if (exception.kind === 'delay' && scenario.drivers.some((driver) => driver.id === exception.driverId) && integer(exception.minutes, 1, 60)) return [];
    if (exception.kind === 'missing_pickup' && scenario.orders.some((order) => order.id === exception.orderId)) return [];
    return ['invalid_exception'];
  }

  // A driver picks the whole compatible batch at the depot, then visits its orders in ID order.
  function planRoutes(scenario, exception) {
    const errors = validateScenario(scenario);
    if (errors.length) return { valid: false, errors };
    const exceptionErrors = validateException(scenario, exception);
    if (exceptionErrors.length) return { valid: false, errors: exceptionErrors };
    const assignmentResult = assignOrders(scenario);
    const batchResult = buildBatches(scenario, assignmentResult.assignments);
    const batches = copy(batchResult.batches);
    const heldOrders = [];
    if (exception && exception.kind === 'missing_pickup') {
      heldOrders.push({ orderId: exception.orderId, status: 'missing_pickup', arrivalMinutes: null });
      for (const batch of batches) {
        batch.orderIds = batch.orderIds.filter((id) => id !== exception.orderId);
        batch.units = batch.orderIds.reduce((sum, id) => sum + scenario.orders.find((order) => order.id === id).units, 0);
      }
    }
    const activeBatches = batches.filter((batch) => batch.orderIds.length);
    const routes = [];
    for (const driver of scenario.drivers.slice().sort((a, b) => a.id.localeCompare(b.id))) {
      let elapsed = 0;
      let delayApplied = false;
      const stops = [];
      const driverBatches = activeBatches.filter((batch) => batch.driverId === driver.id);
      driverBatches.forEach((batch, index) => {
        if (index > 0) elapsed += scenario.returnMinutes;
        const pickups = {};
        for (const id of batch.orderIds) {
          const order = scenario.orders.find((entry) => entry.id === id);
          const wait = Math.max(0, order.readyMinutes - elapsed);
          elapsed += wait;
          const pickupStart = elapsed;
          elapsed += order.pickMinutes;
          pickups[id] = { waitMinutes: wait, pickupStartMinutes: pickupStart, pickupEndMinutes: elapsed };
        }
        batch.departureMinutes = elapsed;
        for (const id of batch.orderIds) {
          const order = scenario.orders.find((entry) => entry.id === id);
          let delayMinutes = 0;
          if (!delayApplied && exception && exception.kind === 'delay' && exception.driverId === driver.id) {
            delayMinutes = exception.minutes;
            elapsed += delayMinutes;
            delayApplied = true;
          }
          elapsed += order.travelMinutes;
          stops.push({ orderId: id, driverId: driver.id, batchId: batch.id, group: order.group, units: order.units,
            ...pickups[id], pickMinutes: order.pickMinutes, travelMinutes: order.travelMinutes,
            delayMinutes, arrivalMinutes: elapsed, clockMinutes: scenario.startMinutes + elapsed });
        }
      });
      routes.push({ driverId: driver.id, capacity: driver.capacity, finishMinutes: elapsed, stops });
    }
    return { valid: true, simulation: true, assignments: assignmentResult.assignments, batches: activeBatches, routes, heldOrders,
      elapsedMinutes: Math.max(0, ...routes.map((route) => route.finishMinutes)) };
  }

  function createSession(scenario) {
    return { simulation: true, version: VERSION, scenario: copy(scenario || createScenario()), stageIndex: -1,
      elapsedMinutes: 0, exception: null, reviewDecision: null, plan: null, events: [], completed: false };
  }

  function appendEvent(state, type, key, params) {
    state.events.push({ sequence: state.events.length + 1, stage: STAGES[state.stageIndex], type,
      elapsedMinutes: state.elapsedMinutes, clockMinutes: state.scenario.startMinutes + state.elapsedMinutes,
      messageKey: key, params: params || {} });
  }

  function formatClock(totalMinutes) {
    const day = Math.floor(totalMinutes / 1440);
    const minuteOfDay = ((totalMinutes % 1440) + 1440) % 1440;
    return String(Math.floor(minuteOfDay / 60)).padStart(2, '0') + ':' + String(minuteOfDay % 60).padStart(2, '0') + (day > 0 ? ' (+' + day + 'd)' : '');
  }

  function stageOutput(state) {
    const scenario = state.scenario;
    switch (state.stageIndex) {
      case 0: return { key: 'intake_output', params: { orders: scenario.orders.length, drivers: scenario.drivers.length } };
      case 1: return { key: 'assignment_output', params: { assignments: assignOrders(scenario).assignments.map((entry) => entry.orderId + ' → ' + entry.driverId).join('; ') } };
      case 2: return { key: 'pickup_output', params: { minutes: scenario.orders.reduce((sum, order) => sum + order.pickMinutes, 0) } };
      case 3: return { key: 'batching_output', params: { count: buildBatches(scenario).batches.length } };
      case 4: return { key: 'routing_output', params: { clock: formatClock(scenario.startMinutes + state.plan.elapsedMinutes), elapsed: state.plan.elapsedMinutes } };
      case 5: return { key: 'delivery_output', params: { arrivals: state.plan.routes.reduce((sum, route) => sum + route.stops.length, 0), held: state.plan.heldOrders.length } };
      case 6: return { key: state.reviewDecision ? (state.reviewDecision.decision === 'approve' ? 'review_accepted' : 'review_held') : 'review_pending', params: {} };
      case 7: return { key: 'learning_output', params: {} };
      default: return { key: 'ready', params: {} };
    }
  }

  function transition(previous, action) {
    if (!previous || previous.simulation !== true || !action) return { ok: false, error: 'error_action' };
    if (action.type === 'reset') return { ok: true, state: createSession(previous.scenario) };
    const errors = validateScenario(previous.scenario);
    if (errors.length) return { ok: false, error: 'error_scenario', validationErrors: errors };
    const state = copy(previous);
    if (action.type === 'exception') {
      if (state.stageIndex !== 4) return { ok: false, error: 'error_exception_stage' };
      if (state.exception) return { ok: false, error: 'error_exception_exists' };
      if (action.kind === 'delay') state.exception = { kind: 'delay', driverId: 'SYN-D1', minutes: 10 };
      else if (action.kind === 'missing_pickup') state.exception = { kind: 'missing_pickup', orderId: 'SYN-O3' };
      else return { ok: false, error: 'error_action' };
      state.plan = planRoutes(state.scenario, state.exception);
      if (!state.plan.valid) return { ok: false, error: 'error_scenario' };
      appendEvent(state, 'exception_introduced', state.exception.kind === 'delay' ? 'exception_delay' : 'exception_missing');
      return { ok: true, state };
    }
    if (action.type === 'review') {
      if (state.stageIndex !== 6 || state.reviewDecision || !['approve', 'hold'].includes(action.decision)) return { ok: false, error: 'error_action' };
      if (action.decision === 'approve' && state.exception && state.exception.kind === 'missing_pickup') return { ok: false, error: 'error_missing_cannot_approve' };
      state.reviewDecision = { decision: action.decision, actor: 'learner_choice_in_this_browser', identityVerified: false,
        elapsedMinutes: state.elapsedMinutes, operationalAuthorization: false };
      appendEvent(state, 'learner_review', action.decision === 'approve' ? 'review_accepted' : 'review_held');
      return { ok: true, state };
    }
    if (action.type !== 'next' || state.completed || state.stageIndex >= 7) return { ok: false, error: 'error_action' };
    if (state.stageIndex === 6 && !state.reviewDecision) return { ok: false, error: 'error_review_required' };
    state.stageIndex += 1;
    if (state.stageIndex === 4) state.plan = planRoutes(state.scenario, state.exception);
    if (state.stageIndex === 5) state.elapsedMinutes = state.plan.elapsedMinutes;
    if (state.stageIndex === 7) state.completed = true;
    const output = stageOutput(state);
    appendEvent(state, 'stage_completed', output.key, output.params);
    return { ok: true, state };
  }

  function exportLearning(state) {
    if (!state || !state.completed || state.stageIndex !== 7 || !state.reviewDecision) return { valid: false, error: 'error_export' };
    const approved = state.reviewDecision.decision === 'approve';
    return { valid: true, record: {
      schema: 'enoch-world-tcn-learning', version: VERSION, simulation: true,
      provenance: 'New website learning simulation; canonical TCN repository integration pending.',
      scenario: copy(state.scenario), plan: copy(state.plan), exception: copy(state.exception), reviewDecision: copy(state.reviewDecision),
      events: copy(state.events),
      outcomes: state.scenario.orders.map((order) => ({ orderId: order.id,
        status: state.exception && state.exception.kind === 'missing_pickup' && state.exception.orderId === order.id ? 'held_missing_pickup' : approved ? 'accepted_simulation_record' : 'held_for_followup',
        realDeliveryPerformed: false })),
      learning: { status: 'unassessed', credentialIssued: false, instructorAssessmentRequired: true,
        competencies: ['capacity_and_compatibility', 'readiness_and_arrival_arithmetic', 'exception_review_judgment'].map((id) => ({ id, status: 'unassessed', score: null, assessor: null })),
        rubric: { id: null, version: null, criteria: null, passed: null } },
      boundaries: { realDispatch: false, realOrders: false, tokenRequired: false, fundsMoved: false, accreditationClaim: false,
        etaGuarantee: false, networkUsed: false, persistentStorageUsed: false }
    } };
  }

  function enhance(root) {
    if (!root || root.dataset.tcnReady === 'true') return;
    const get = (name) => root.querySelector('[data-tcn-' + name + ']');
    const nodes = {};
    const required = ['settings', 'next', 'reset', 'export', 'status', 'fallback', 'stages', 'exceptions', 'delay', 'missing', 'exception-note', 'plan', 'batches', 'route-rows', 'review', 'review-help', 'approve', 'hold', 'log-panel', 'log', 'learning'];
    for (const name of required) {
      nodes[name] = get(name);
      if (!nodes[name]) return;
    }
    const controls = ['tcn-start', 'tcn-readiness', 'tcn-capacity-1', 'tcn-capacity-2'].map((id) => root.querySelector('#' + id));
    if (controls.some((control) => !control)) return;
    let translations = {};
    const translationNode = document.getElementById('ew-tcn-translations');
    if (translationNode) {
      try { const parsed = JSON.parse(translationNode.textContent); if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) translations = parsed; } catch (_) { /* English remains available. */ }
    }
    const t = (key, params) => {
      let message = typeof translations[key] === 'string' ? translations[key] : ENGLISH[key] || key;
      for (const [name, value] of Object.entries(params || {})) message = message.split('{' + name + '}').join(String(value));
      return message;
    };
    root.querySelectorAll('[data-tcn-t]').forEach((node) => { node.textContent = t(node.dataset.tcnT); });
    root.querySelectorAll('[data-tcn-aria]').forEach((node) => { node.setAttribute('aria-label', t(node.dataset.tcnAria)); });
    let state = null;
    const controller = new AbortController();
    const listen = (node, event, handler) => node.addEventListener(event, handler, { signal: controller.signal });
    const announce = (message) => { nodes.status.textContent = message; };
    const readScenario = () => createScenario({ startMinutes: Number(controls[0].value), readiness: controls[1].value, capacity1: Number(controls[2].value), capacity2: Number(controls[3].value) });
    const syncReadiness = () => { const node = root.querySelector('[data-tcn-ready-value]'); if (node) node.textContent = controls[1].value === 'late' ? '25' : '10'; };
    const textElement = (tag, value) => { const node = document.createElement(tag); node.textContent = value; return node; };

    function renderPlan() {
      nodes['route-rows'].replaceChildren();
      nodes.batches.replaceChildren();
      if (!state || !state.plan) return;
      for (const batch of state.plan.batches) nodes.batches.append(textElement('p', t('batch_summary', {
        batch: batch.id, orders: batch.orderIds.join(', '), group: t('group_' + batch.group), units: batch.units, capacity: batch.capacity,
        clock: formatClock(state.scenario.startMinutes + batch.departureMinutes)
      })));
      const outcome = state.reviewDecision ? (state.reviewDecision.decision === 'approve' ? 'accepted' : 'held') : state.stageIndex >= 5 ? 'provisional' : 'planned';
      for (const route of state.plan.routes) {
        for (const stop of route.stops) {
          const row = document.createElement('tr');
          const values = [stop.driverId, stop.batchId, stop.orderId, t('minutes', { value: stop.waitMinutes }), t('minutes', { value: stop.pickMinutes }), t('minutes', { value: stop.travelMinutes }), formatClock(stop.clockMinutes) + ' (+' + stop.arrivalMinutes + ')', t(outcome)];
          values.forEach((value, index) => { const cell = textElement(index === 2 ? 'th' : 'td', value); if (index === 2) cell.scope = 'row'; row.append(cell); });
          nodes['route-rows'].append(row);
        }
      }
      for (const held of state.plan.heldOrders) {
        const row = document.createElement('tr');
        ['—', '—', held.orderId, '—', '—', '—', t('no_arrival'), t('missing_pickup')].forEach((value, index) => { const cell = textElement(index === 2 ? 'th' : 'td', value); if (index === 2) cell.scope = 'row'; row.append(cell); });
        nodes['route-rows'].append(row);
      }
    }

    function render() {
      const index = state ? state.stageIndex : -1;
      nodes.settings.disabled = index >= 0;
      nodes.next.disabled = index === 7 || (index === 6 && !state.reviewDecision);
      nodes.next.textContent = index < 0 ? t('start') : index === 7 ? t('finished') : index === 6 ? t('capture') : t('next', { stage: t('stage_' + STAGES[index + 1]) });
      nodes.reset.disabled = false;
      nodes.export.disabled = index !== 7;
      nodes.exceptions.hidden = index !== 4;
      nodes.delay.disabled = index !== 4 || Boolean(state && state.exception);
      nodes.missing.disabled = index !== 4 || Boolean(state && state.exception);
      const kind = state && state.exception ? state.exception.kind : 'none';
      nodes['exception-note'].textContent = t(kind === 'missing_pickup' ? 'exception_missing' : 'exception_' + kind);
      nodes.plan.hidden = !state || !state.plan;
      nodes.review.hidden = index !== 6 || Boolean(state && state.reviewDecision);
      nodes.approve.hidden = kind === 'missing_pickup';
      nodes.approve.disabled = index !== 6 || Boolean(state && state.reviewDecision) || kind === 'missing_pickup';
      nodes.hold.disabled = index !== 6 || Boolean(state && state.reviewDecision);
      nodes['review-help'].textContent = t(kind === 'delay' ? 'review_delay_help' : kind === 'missing_pickup' ? 'review_missing_help' : 'review_normal_help');
      nodes['log-panel'].hidden = index < 0;
      nodes.learning.hidden = index !== 7;
      root.querySelectorAll('[data-tcn-stage]').forEach((node) => {
        const stage = Number(node.dataset.tcnStage);
        if (stage === index) node.setAttribute('aria-current', 'step'); else node.removeAttribute('aria-current');
        node.classList.toggle('tcn-finished', stage < index || index === 7);
        const outputNode = node.querySelector('[data-tcn-output]');
        if (!outputNode) return;
        const event = state ? state.events.slice().reverse().find((entry) => entry.stage === STAGES[stage] && entry.type !== 'exception_introduced') : null;
        outputNode.textContent = event ? t(event.messageKey, event.params) : '';
        if (stage === 4 && state && state.plan) {
          const result = stageOutput({ ...state, stageIndex: 4 });
          outputNode.textContent = t(result.key, result.params);
        }
      });
      nodes.log.replaceChildren();
      if (state) state.events.forEach((event) => {
        const item = document.createElement('li');
        item.append(textElement('strong', t('log_time', { elapsed: event.elapsedMinutes, clock: formatClock(event.clockMinutes) }) + ' · '));
        item.append(document.createTextNode(t('stage_' + event.stage) + ': ' + t(event.messageKey, event.params)));
        nodes.log.append(item);
      });
      renderPlan();
    }

    function act(action) {
      if (!state) state = createSession(readScenario());
      const result = transition(state, action);
      if (!result.ok) { announce(t(result.error)); return; }
      state = result.state;
      render();
      const output = stageOutput(state);
      announce(action.type === 'exception' ? t(state.exception.kind === 'delay' ? 'exception_delay' : 'exception_missing') : t('stage_status', { number: state.stageIndex + 1, stage: t('stage_' + STAGES[state.stageIndex]), output: t(output.key, output.params) }));
      if (state.stageIndex === 6 && !state.reviewDecision) (nodes.approve.hidden ? nodes.hold : nodes.approve).focus();
      if (action.type === 'review') nodes.next.focus();
    }

    // Wire every handler before enabling or revealing any interactive control.
    listen(nodes.next, 'click', () => act({ type: 'next' }));
    listen(nodes.delay, 'click', () => act({ type: 'exception', kind: 'delay' }));
    listen(nodes.missing, 'click', () => act({ type: 'exception', kind: 'missing_pickup' }));
    listen(nodes.approve, 'click', () => act({ type: 'review', decision: 'approve' }));
    listen(nodes.hold, 'click', () => act({ type: 'review', decision: 'hold' }));
    listen(controls[1], 'change', syncReadiness);
    listen(nodes.reset, 'click', () => {
      state = null;
      controls[0].value = '480'; controls[1].value = 'normal'; controls[2].value = '4'; controls[3].value = '3';
      syncReadiness(); render(); announce(t('reset_done')); nodes.next.focus();
    });
    listen(nodes.export, 'click', () => {
      const result = exportLearning(state);
      if (!result.valid) { announce(t(result.error)); return; }
      let url;
      let link;
      try {
        const blob = new Blob([JSON.stringify(result.record, null, 2) + '\n'], { type: 'application/json' });
        url = URL.createObjectURL(blob);
        link = document.createElement('a');
        link.href = url; link.download = 'tcn-synthetic-learning-v1.json'; link.hidden = true;
        root.append(link); link.click(); link.remove();
        // A brief release delay lets browsers consume the download; no network or persistence is used.
        global.setTimeout(() => URL.revokeObjectURL(url), 1000);
        announce(t('download_ready'));
      } catch (_) {
        if (link) link.remove();
        if (url) URL.revokeObjectURL(url);
        announce(t('download_failed'));
      }
    });
    render();
    syncReadiness();
    nodes.fallback.hidden = true;
    root.querySelectorAll('[data-tcn-enhanced]').forEach((node) => { node.hidden = false; });
    root.dataset.tcnReady = 'true';
    announce(t('ready'));
  }

  const API = Object.freeze({ VERSION, STAGES, ENGLISH, createScenario, validateScenario, assignOrders, buildBatches, planRoutes, createSession, transition, exportLearning, formatClock });
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  global.EnochTcnSimulation = API;
  if (typeof document !== 'undefined') {
    const initialize = () => enhance(document.getElementById('ew-tcn-simulator'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true }); else initialize();
    document.addEventListener('shopify:section:load', initialize);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);

